import { ConfigProvider, Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { listSandboxKeys } from '../../services/account/api';
import { createCaptureTransaction } from '../../services/application/api';
import { useLayoutEffect, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/auth';
import { FormattedMessage } from 'react-intl';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import './styles.css';
import { FormScreen } from './screens/FormScreen';
import { QrCodeScreen } from './screens/QrCodeScreen';
import { WhatsappInputScreen } from './screens/WhatsappInputScreen';
import { WhatsappSuccessScreen } from './screens/WhatsappSuccessScreen';
import { HtmlContentScreen } from './screens/HtmlContentScreen';

export function PreCaptureInformationDialog({ open, onCancel, primaryColor }) {
  const { user, currentWorkspace, workspaces } = useAuth();

  const uuid = crypto.randomUUID();

  const [captureKey, setCaptureKey] = useState();
  const [testCaptureUserIdentifier, setTestCaptureUserIdentifier] =
    useLocalStorage('testCaptureUserIdentifier', user?.email || '');
  const [testCaptureTransactionType, setTestCaptureTransactionType] =
    useLocalStorage('testCaptureTransactionType', 'verification');
  const [lastCaptureType, setLastCaptureType] = useLocalStorage(
    'lastCaptureType',
    'start_capture',
  );

  const [email, setEmail] = useState(testCaptureUserIdentifier);
  const [transactionType, setTransactionType] = useState(
    testCaptureTransactionType,
  );
  const [endToEndId, setEndToEndId] = useState(uuid);
  const [isLoading, setLoading] = useState(true);
  const [isNavigating, setIsNavigating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [captureType, setCaptureType] = useState(lastCaptureType);
  const [currentScreen, setCurrentScreen] = useState('form');
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [contactName, setContactName] = useState(user?.name || '');
  const [companyName, setCompanyName] = useState(
    workspaces?.find(ws => ws.external_id === currentWorkspace)?.name || '',
  );
  const [captureResponse, setCaptureResponse] = useState(null);

  useEffect(() => {
    if (user?.email && !testCaptureUserIdentifier) {
      setTestCaptureUserIdentifier(user.email);
      setEmail(user.email);
    }
  }, [user?.email, testCaptureUserIdentifier, setTestCaptureUserIdentifier]);

  useEffect(() => {
    if (user?.name && !contactName) {
      setContactName(user.name);
    }
  }, [user?.name, contactName]);

  useEffect(() => {
    if (workspaces && currentWorkspace && !companyName) {
      const workspace = workspaces.find(
        ws => ws.external_id === currentWorkspace,
      );
      if (workspace?.name) {
        setCompanyName(workspace.name);
      }
    }
  }, [workspaces, currentWorkspace, companyName]);

  useLayoutEffect(() => {
    Promise.all([
      listSandboxKeys(currentWorkspace).then(res => {
        setCaptureKey(res.capture_key);
      }),
    ]).finally(() => setLoading(false));
  }, [currentWorkspace]);

  const buildCapturePayload = () => {
    const basePayload = {
      capture_key: captureKey,
      user_identifier: email,
      type: transactionType,
      end_to_end_id: endToEndId,
      send_results_to: {
        media: 'email',
        email: user?.email || email,
      },
    };

    switch (captureType) {
      case 'whatsapp': {
        return {
          ...basePayload,
          send_capture_to: {
            media: 'whatsapp',
            contact_name: contactName,
            company_name: companyName,
            phone_number: phoneNumber?.valid
              ? `+${phoneNumber.countryCode}${phoneNumber.areaCode || ''}${phoneNumber.phoneNumber}`
              : '',
          },
        };
      }
      case 'qr_code':
        return {
          ...basePayload,
          return_type: 'capture_url',
        };
      case 'start_capture':
        return basePayload;
      default:
        return basePayload;
    }
  };

  const startNewCapture = async () => {
    if (captureType === 'whatsapp') {
      setCurrentScreen('whatsapp');
      return;
    }

    setIsNavigating(true);
    try {
      const payload = buildCapturePayload();
      const response = await createCaptureTransaction(payload);
      setCaptureResponse(response);

      if (captureType === 'qr_code') {
        setCurrentScreen('qr_code');
      } else if (captureType === 'start_capture') {
        if (
          typeof response === 'string' &&
          response.trim().toLowerCase().startsWith('<!doctype html')
        ) {
          setCurrentScreen('html_content');
        } else {
          console.error(
            'HTML content not available in response for start_capture or response is not a string. Response:',
            response,
          );
          handleModalClose();
        }
      } else {
        handleModalClose();
      }
    } catch (error) {
      console.error('Error creating capture transaction:', error);
    } finally {
      if (captureType !== 'whatsapp') {
        setIsNavigating(false);
      }
    }
  };

  const handleMenuClick = e => {
    setCaptureType(e.key);
    setLastCaptureType(e.key);
  };

  const handleModalClose = () => {
    setCurrentScreen('form');
    setPhoneNumber(null);
    setIsNavigating(false);
    setIsCopied(false);
    onCancel();
  };

  const generateCaptureLink = () => {
    const baseUrl = window.location.origin;
    const params = new URLSearchParams({
      captureKey,
      email,
      endToEndId,
      transactionType,
      captureType,
    });
    return `${baseUrl}/capture?${params.toString()}`;
  };

  const copyToClipboard = async textToCopy => {
    try {
      const text = textToCopy || generateCaptureLink();
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Erro ao copiar para clipboard:', err);
    }
  };

  const sendWhatsApp = async () => {
    setIsNavigating(true);
    try {
      const payload = buildCapturePayload();
      await createCaptureTransaction(payload);
      setCurrentScreen('whatsapp_success');
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
    } finally {
      setIsNavigating(false);
    }
  };

  const menuItems = [
    {
      label: <FormattedMessage id="start_capture" />,
      key: 'start_capture',
    },
    {
      label: <FormattedMessage id="qr_code" />,
      key: 'qr_code',
    },
    {
      label: <FormattedMessage id="whatsapp" />,
      key: 'whatsapp',
    },
  ];

  const handleEmailChange = newEmail => {
    setTestCaptureUserIdentifier(newEmail);
    setEmail(newEmail);
  };

  const handleTransactionTypeChange = newTransactionType => {
    setTestCaptureTransactionType(newTransactionType);
    setTransactionType(newTransactionType);
  };

  const handleEndToEndIdChange = newEndToEndId => {
    setEndToEndId(newEndToEndId);
  };

  const handlePhoneNumberChange = newPhoneNumberValue => {
    setPhoneNumber(newPhoneNumberValue);
  };

  const handleContactNameChange = newContactName => {
    setContactName(newContactName);
  };

  const handleCompanyNameChange = newCompanyName => {
    setCompanyName(newCompanyName);
  };

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: primaryColor },
        components: {
          Form: {
            marginLG: 15,
          },
          Button: {
            borderColorDisabled: 'transparent',
          },
        },
      }}
    >
      <Modal
        rootClassName="pre-capture"
        centered
        destroyOnHidden
        open={open}
        onOk={handleModalClose}
        onCancel={handleModalClose}
        className="w-full max-w-full sm:max-w-[480px]"
        footer={null}
        closeIcon={
          currentScreen === 'html_content' ? null : <div className="close" />
        }
        mousePosition={{ x: '50%' }}
      >
        <div
          className={
            currentScreen === 'html_content'
              ? 'flex-grow flex flex-col h-full w-full'
              : 'pt-4'
          }
        >
          {isLoading ? (
            <div className="flex justify-center items-center min-h-[600px]">
              <Spin
                size="large"
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 50 }}
                    spin
                  />
                }
              />
            </div>
          ) : isNavigating ? (
            <div className="flex flex-col justify-center items-center min-h-[600px]">
              <Spin
                size="large"
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 100 }}
                    spin
                  />
                }
              />
              <p className="mt-4 text-gray-600">
                {captureType === 'qr_code' && currentScreen === 'form' && (
                  <FormattedMessage id="generating_qr_code" />
                )}
                {currentScreen === 'whatsapp' && (
                  <FormattedMessage id="sending_message" />
                )}
              </p>
            </div>
          ) : (
            <>
              {currentScreen === 'form' && (
                <FormScreen
                  captureKey={captureKey}
                  email={email}
                  transactionType={transactionType}
                  endToEndId={endToEndId}
                  primaryColor={primaryColor}
                  handleEmailChange={handleEmailChange}
                  handleTransactionTypeChange={handleTransactionTypeChange}
                  handleEndToEndIdChange={handleEndToEndIdChange}
                  menuItems={menuItems}
                  captureType={captureType}
                  handleMenuClick={handleMenuClick}
                  startNewCapture={startNewCapture}
                  formButtonDisabled={!endToEndId || !email}
                />
              )}

              {currentScreen === 'qr_code' && (
                <QrCodeScreen
                  captureResponse={captureResponse}
                  isCopied={isCopied}
                  copyToClipboard={copyToClipboard}
                  handleModalClose={handleModalClose}
                  primaryColor={primaryColor}
                />
              )}

              {currentScreen === 'whatsapp' && (
                <WhatsappInputScreen
                  primaryColor={primaryColor}
                  phoneNumber={phoneNumber}
                  handlePhoneNumberChange={handlePhoneNumberChange}
                  contactName={contactName}
                  handleContactNameChange={handleContactNameChange}
                  companyName={companyName}
                  handleCompanyNameChange={handleCompanyNameChange}
                  sendWhatsApp={sendWhatsApp}
                  isNavigating={isNavigating}
                />
              )}

              {currentScreen === 'whatsapp_success' && (
                <WhatsappSuccessScreen
                  primaryColor={primaryColor}
                  captureResponse={captureResponse}
                  handleModalClose={handleModalClose}
                />
              )}

              {currentScreen === 'html_content' && (
                <HtmlContentScreen
                  captureResponse={captureResponse}
                  handleModalClose={handleModalClose}
                />
              )}
            </>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
}
